import {
  AfterViewInit,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  inject,
  makeStateKey,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import {
  combineLatest,
  Observable,
  of,
  Subject,
  switchMap,
  Subscription,
  NEVER,
  buffer,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsCollection, CmsOffer, Location } from '@fcom/core-api';
import {
  ConfigService,
  GlobalBookingTravelClass,
  isEmpty,
  LocationRouteCffService,
  StateTransferService,
  stopPropagation,
} from '@fcom/core';
import {
  ConsentTextId,
  DestinationClickTarget,
  FinnairThemeService,
  PageMetaService,
  PriceCalendarCTAParams,
  PriceCalendarParams,
  PriceCalendarService,
  RecommendationService,
  TripType,
} from '@fcom/common';
import { DataCloudService } from '@fcom/common/datacloud';
import {
  PersonalizedDestinationLocation,
  TrendingDestinationLocation,
} from '@fcom/common/interfaces/recommendation.interface';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { unsubscribe } from '@fcom/core/utils';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';
import { getOfferWithAmDestination } from '@fcom/common/utils';
import { isShortHaulCountry } from '@fcom/common/utils/flight.utils';
import { ConsentService } from '@fcom/common/login';
import {
  GtmEvent,
  GA4Product,
  GA4Affiliation,
  GA4Category,
  GA4CreativeName,
  GA4TripType,
  GA4HaulType,
} from '@fcom/common/interfaces/gtm.interface';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { BlockOfferData, MarketingOffer, MarketingOfferPrices } from '../../interfaces';
import { CheapestPriceForAllDestinationsService, toPromoBlock } from '../../services';
import { FetchPriorityEnum } from '../../../ui-components';

const TRENDING_BADGE_LABEL = 'popularNow';
const ORIGIN_LOCATION_STATE_KEY = makeStateKey<Location>('trending-dest-origin');
enum GA4CreativeSlot {
  NAVIGATE_TO_DESTINATION = 'navigate-to-destination',
  SELECT_FLIGHT_HOTEL = 'select-flight-hotel',
  SELECT_DATES = 'select-dates',
}

@Component({
  selector: 'fin-trending-destinations-grid',
  template: `
    @let isPersonalizedDestinationsTestVariant = isPersonalizedDestinationsTestVariant$ | async;
    <fin-offer-grid
      *ngIf="originLocation$ | async as originLocation"
      [title]="
        isPersonalizedDestinationsTestVariant
          ? ('personalizedDestinations.title' | finLocalization)
          : content.teaserTitle
      "
      [offers$]="offers$"
      [cmsTags]="content.subjectTaxonomyTags || []"
      [tripType]="tripType"
      (destinationClicked)="destinationClick(originLocation, $event)"
      (scrolledPastDestination)="scrollEvents$.next($event)"
      (flightHotelClicked)="openAmContinueModal(originLocation, $event)"
      (calendarClicked)="calendarClicked(originLocation, $event)"
      [mobileCarousel]="isPersonalizedDestinationsTestVariant"
      [noHeaderPadding]="isPersonalizedDestinationsTestVariant"
      [fetchImagePriority]="FetchPriorityEnum.low"
    />
    <fin-am-destination-continue-modal
      [open]="isAmContinueModalOpen"
      (closeModal)="closeAmContinueModal()"
      [bookUrl]="flightHotelUrl"
    />
    <fcom-modal
      [open]="(calendarOpen$ | async) !== null"
      [fitToContent]="true"
      [buttons]="null"
      [roundedContent]="true"
      [fullHeight]="true"
      [extraWide]="true"
      (close)="closePriceCalendar()"
      data-testid="price-calendar-modal"
    >
      <fin-price-calendar
        [openWithParams$]="calendarOpen$"
        [showAddReturn]="true"
        [showSubtitle]="true"
        [ctaLabel]="'bookingSearch.search' | finLocalization"
        (modalClose)="closePriceCalendar()"
        (ctaClicked)="priceCalendarCTAClicked($event)"
      />
    </fcom-modal>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TrendingDestinationsComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly FetchPriorityEnum = FetchPriorityEnum;
  private recommendationService = inject(RecommendationService);
  private pageMetaService = inject(PageMetaService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private cheapestPriceForAllDestinationsService = inject(CheapestPriceForAllDestinationsService);
  private amDestinationService = inject(AmDestinationService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private priceCalendarService = inject(PriceCalendarService);
  private dataCloudService = inject(DataCloudService);
  private consentService = inject(ConsentService);
  private configService = inject(ConfigService);
  private injector = inject(Injector);
  private platform = inject(PLATFORM_ID);
  private stateTransferService = inject(StateTransferService);
  private themeService = inject(FinnairThemeService);

  @Input()
  content: CmsCollection;

  offers$: Observable<BlockOfferData[]>;
  scrollEvents$ = new Subject<{ offer: BlockOfferData; index: number }>();
  flightHotelUrl: string;
  tripType: TripType;
  originLocation$: Observable<Location>;
  calendarOpen$: Observable<PriceCalendarParams | null> = NEVER;
  isPersonalizedDestinationsTestVariant$: Observable<boolean>;

  isAmContinueModalOpen = false;

  readonly popularTrendLimit = 2;
  readonly maxNumberOfOffers = 8;
  readonly minNumberOfOffers = 4;

  readonly isNewBrand = computed(() => {
    return this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  });

  private isPersonalizedSignal: Signal<boolean>;
  private subscriptions = new Subscription();

  constructor() {
    this.calendarOpen$ = this.priceCalendarService.calendarOpen$;
    this.originLocation$ = this.stateTransferService.wrapToStateCache(ORIGIN_LOCATION_STATE_KEY, () =>
      this.pageMetaService.defaultOriginLocationCode$.pipe(
        filter(Boolean),
        switchMap((code) => this.locationRouteCffService.bestGuessFor(code, this.languageService.localeValue))
      )
    );
  }

  ngOnInit(): void {
    this.tripType = this.getTripType();

    this.isPersonalizedDestinationsTestVariant$ = isPlatformServer(this.platform)
      ? of(false)
      : this.consentService.getCookieConsentStatusById(ConsentTextId.COOKIE_PERSONALIZATION).pipe(
          distinctUntilChanged(),
          withLatestFrom(this.languageService.countryCode),
          switchMap(([consent, country]) =>
            of(!!consent && this.configService.cfg.isPersonalisedDestinationsEnabled && country === 'FI')
          ),
          finShare()
        );

    this.isPersonalizedSignal = toSignal(this.isPersonalizedDestinationsTestVariant$, {
      initialValue: false,
      injector: this.injector,
    });

    this.offers$ = combineLatest([
      this.originLocation$.pipe(map((loc) => loc.locationCode)),
      this.cheapestPriceForAllDestinationsService.bothWayOffers(),
      this.isPersonalizedDestinationsTestVariant$,
    ]).pipe(
      switchMap(([origin, offers, isPersonalizedDestinationsTestVariant]) =>
        (isPersonalizedDestinationsTestVariant
          ? this.getPersonalizedDestinations(origin)
          : this.recommendationService.getTrendingDestinations(origin).pipe(catchError(() => of([])))
        ).pipe(
          // filter out current pageMeta location if found and convert first n items to BlockOfferData[]
          map((locations) => {
            const filteredDestination = locations
              .filter((item: TrendingDestinationLocation) => item.locationCode !== origin)
              .map((location: TrendingDestinationLocation) =>
                this.trendingToOffer(location, ...this.getBadgeLabelAndIcon(location))
              )
              .filter((item: BlockOfferData) => this.flightPriceFilter(item, offers, this.tripType))
              .map((item: BlockOfferData) => this.enrichWithPrice(item, offers))
              .reduce((locations, loc) => {
                // combine same locations but keeps the original value if new value is null/undefined
                locations.set(loc.destination, {
                  ...locations.get(loc.destination),
                  ...Object.fromEntries(
                    Object.entries(loc).map(([key, value]) => [key, value ?? locations.get(loc.destination)?.[key]])
                  ),
                });
                return locations;
              }, new Map());
            const filteredDestinationList = [...filteredDestination.values()];
            return filteredDestinationList.slice(0, this.getAmountOfOffers(filteredDestinationList));
          }),
          // fallback to CMS content if the recommendation api fails to deliver
          switchMap((locations: BlockOfferData[]) =>
            locations?.length > 0
              ? of(locations)
              : this.languageService.lang.pipe(
                  map((lang) => this.content.items.map((offer) => toPromoBlock(offer as CmsOffer, lang))),
                  map((offs) => offs.slice(0, this.getAmountOfOffers(offs)))
                )
          ),
          switchMap((offs) =>
            this.languageService.localeValue.includes('FI')
              ? getOfferWithAmDestination(
                  offs,
                  this.amDestinationService.amDestinationList(),
                  this.locationRouteCffService
                )
              : of(offs)
          ),
          distinctUntilChanged(),
          finShare()
        )
      ),
      finShare()
    );
  }

  /**
   * tracks destination codes that have already been reported to analytics to prevent duplicate
   * tracking events when destinations are repeatedly scrolled into view.
   */
  private trackedDestinations = new Set<string>();

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform)) {
      this.subscriptions.add(
        this.scrollEvents$
          .pipe(
            buffer(this.scrollEvents$.pipe(debounceTime(200))),
            map((items) => items.filter((item) => !this.trackedDestinations.has(item.offer.destination))),
            filter((items) => !isEmpty(items)),
            map((items) => [...items].sort((a, b) => a.index - b.index)),
            withLatestFrom(this.originLocation$)
          )
          .subscribe(([items, originLocation]) => {
            items.forEach((item) => this.trackedDestinations.add(item.offer.destination));
            this.gtmService.ecommerceEventGA4(
              GtmEvent.VIEW_ITEM_LIST,
              items.map((item) =>
                this.mapOfferToGA4Product(item.offer, item.index, originLocation, this.isPersonalizedSignal())
              )
            );
          })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  destinationClick(originLocation: Location, [destinationItem, index]: [BlockOfferData, number]): void {
    this.gtmService.ecommerceEventGA4(GtmEvent.SELECT_ITEM, [
      this.mapOfferToGA4Product(
        destinationItem,
        index,
        originLocation,
        this.isPersonalizedSignal(),
        GA4CreativeSlot.NAVIGATE_TO_DESTINATION
      ),
    ]);
    this.dataCloudService.destinationListDestinationClick(
      originLocation.locationCode,
      destinationItem.destination,
      DestinationClickTarget.DESTINATION_PAGE
    );
  }

  openAmContinueModal(
    originLocation: Location,
    {
      amLink,
      offer,
      index,
    }: { amLink: string; destinationTitle: string; destinationCode: string; offer: BlockOfferData; index: number }
  ): void {
    this.gtmService.ecommerceEventGA4(GtmEvent.SELECT_ITEM, [
      this.mapOfferToGA4Product(
        offer,
        index,
        originLocation,
        this.isPersonalizedSignal(),
        GA4CreativeSlot.SELECT_FLIGHT_HOTEL
      ),
    ]);

    this.dataCloudService.destinationListDestinationClick(
      originLocation.locationCode,
      offer.destination,
      DestinationClickTarget.FLIGHT_PLUS_HOTEL
    );
    this.isAmContinueModalOpen = true;
    this.flightHotelUrl = amLink;
  }

  closeAmContinueModal(): void {
    this.isAmContinueModalOpen = false;
  }

  getTripType(): TripType {
    const localTripType = this.content.localSettings?.tripType as string | undefined;
    const tripType = TripType[localTripType?.toUpperCase()] ?? TripType.RETURN;
    return this.configService.cfg.isPersonalisedDestinationsEnabled
      ? TripType.RETURN
      : this.languageService.localeValue.includes('FI')
        ? TripType.ONEWAY
        : tripType;
  }

  calendarClicked(
    originLocation: Location,
    [event, destinationItem, travelClass, tripType, index]: [
      Event,
      BlockOfferData,
      GlobalBookingTravelClass,
      TripType,
      number,
    ]
  ): void {
    stopPropagation(event);

    this.gtmService.ecommerceEventGA4(GtmEvent.SELECT_ITEM, [
      this.mapOfferToGA4Product(
        destinationItem,
        index,
        originLocation,
        this.isPersonalizedSignal(),
        GA4CreativeSlot.SELECT_DATES
      ),
    ]);

    this.priceCalendarService.openPriceCalendar(
      originLocation.locationCode,
      destinationItem.destination,
      tripType,
      travelClass,
      destinationItem.title,
      destinationItem.discountCode,
      false
    );

    this.dataCloudService.destinationListDestinationClick(
      originLocation.locationCode,
      destinationItem.destination,
      DestinationClickTarget.CALENDAR
    );
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }

  private getPersonalizedDestinations(origin: string): Observable<PersonalizedDestinationLocation[]> {
    return this.recommendationService.getPersonalizedDestinations(origin).pipe(
      map((locations) => (isPlatformServer(this.platform) ? [] : locations)),
      catchError(() => of([]))
    );
  }

  // filter out the destination where oneway price is more expensive than return
  private flightPriceFilter(location: BlockOfferData, offers: MarketingOffer[], tripType: TripType): BlockOfferData {
    const priceOffer = this.getPriceOffer(offers, location);
    //skip checking if the destination doesn't have price available

    if (!priceOffer?.find((i) => i.price.tripType === tripType)?.price?.amount) {
      return null;
    }

    // do not check price if trending destination type is return
    if (tripType === TripType.RETURN) {
      return location;
    }

    if (
      Number(priceOffer?.find((i) => i.price.tripType === TripType.ONEWAY)?.price.amount) <
      Number(priceOffer?.find((i) => i.price.tripType === TripType.RETURN)?.price.amount)
    ) {
      return location;
    }
  }

  private getPriceOffer(offers: MarketingOffer[], location: BlockOfferData): MarketingOfferPrices[] {
    return offers
      .find((offer) => offer.destination === location.destination)
      ?.prices.filter((priceOffer) => priceOffer.travelClass === GlobalBookingTravelClass.ECONOMY);
  }

  private getAmountOfOffers(destination: BlockOfferData[]): number {
    const amountOfOfferWithPrice = destination.length;
    // If amount offer with price < 3, then no offer will be shown
    if (amountOfOfferWithPrice < this.minNumberOfOffers) {
      return 0;
    }
    // If amount offer with price < 8 and >=4, then 4 offers will be returned
    if (amountOfOfferWithPrice < this.maxNumberOfOffers) {
      return this.minNumberOfOffers;
    }
    // others return 9 offers
    return this.maxNumberOfOffers;
  }

  private getBadgeLabelAndIcon(location: TrendingDestinationLocation): [string, SvgLibraryIcon] {
    return !location.isIncludedInCampaign && location.trend >= this.popularTrendLimit && !this.isNewBrand()
      ? [TRENDING_BADGE_LABEL, SvgLibraryIcon.TRENDING_ARROW_UP]
      : [null, null];
  }

  private trendingToOffer(
    location: TrendingDestinationLocation | PersonalizedDestinationLocation,
    badgeLabel: string,
    badgeIcon: SvgLibraryIcon
  ): BlockOfferData {
    return {
      title: location.cityName || location.title,
      price: null,
      subtitle: null,
      callToAction: null,
      link: location.destinationUrl,
      flag: location.countryCode,
      imageData: location.picture,
      destination: location.locationCityCode ?? location.locationCode, //destination in marketing offer is locationCityCode if that is available (e.g. REK)
      badgeLabel: location.badgeLabel ?? badgeLabel,
      badgeIcon,
      enableBlackTeaser: location.enableBlackTeaser,
    } as BlockOfferData;
  }

  private enrichWithPrice(offer: BlockOfferData, marketingOffers: MarketingOffer[]): BlockOfferData {
    const prices = this.getPriceOffer(marketingOffers, offer);
    return {
      ...offer,
      onewayPrice: prices?.find((p) => p.price.tripType === TripType.ONEWAY),
      returnPrice: prices?.find((p) => p.price.tripType === TripType.RETURN),
    };
  }

  private mapOfferToGA4Product(
    offer: BlockOfferData,
    index: number,
    originLocation: Location,
    isPersonalized: boolean,
    creativeSlot: string = undefined
  ): GA4Product {
    const tripTypeLabel = this.tripType === TripType.ONEWAY ? GA4TripType.ONEWAY : GA4TripType.RETURN;
    const price = this.tripType === TripType.ONEWAY ? offer.onewayPrice : offer.returnPrice;
    const itemListId = `frontpage-${isPersonalized ? 'personalized' : 'trending'}-destinations-offer`;

    return {
      item_id: `${tripTypeLabel}_${originLocation.locationCode}-${offer.destination}`,
      item_name: `${tripTypeLabel}_${originLocation.locationCode}-${offer.destination}`,
      price: Number(price?.price?.amount ?? 0),
      quantity: 1,
      currency: price?.price?.currencyCode ?? '',
      affiliation: GA4Affiliation.DOTCOM,
      item_category: GA4Category.FLIGHTS,
      item_category2: tripTypeLabel,
      item_category3: undefined,
      item_category4: undefined,
      item_variant: undefined,
      item_haul_type:
        isShortHaulCountry(originLocation.countryCode) && isShortHaulCountry(offer.flag)
          ? GA4HaulType.SHORT
          : GA4HaulType.LONG,
      item_complementary: false,
      item_list_id: itemListId,
      item_list_name: itemListId,
      index: index + 1,
      creative_name: isPersonalized ? GA4CreativeName.SALESFORCE : GA4CreativeName.FINNAIR,
      creative_slot: creativeSlot,
    };
  }
}
